<template>
  <form @submit.prevent="saveData('publish')">
    <div class="flex items-start w-full">
      <section class="sticky flex-none w-1/4 top-24">
        <GeneralTabs
          :names="['AMOQ', 'metric']"
          classes="w-full"
          @tab-changed="viewTab = $event"
        >
          <section
            v-if="viewTab === 'metric'"
            id="section-actions-metrics"
            class="w-full"
          >
            <!-- Emission metrics -->
            <div class="flex flex-col gap-y-3">
              <div
                id="metric-filters"
                class="flex items-center gap-2 mr-1"
              >
                <select
                  v-model="filteredMetricTopic"
                  class="w-full h-8 px-3 py-1 text-sm leading-tight text-gray-700 border border-gray-300 outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                >
                  <option
                    value="all"
                    selected
                  >
                    All
                  </option>
                  <option value="sm">
                    Standard Metric
                  </option>
                  <option
                    v-for="(topic, sectionIndex) in unfilteredMetricTopic"
                    :key="`section-${sectionIndex}`"
                    :value="topic.id"
                  >
                    {{ topic.title }}
                  </option>
                </select>
                <input
                  v-model="metricSearchQuery"
                  class="w-full h-8 px-3 py-1 leading-tight text-gray-700 border border-gray-300 outline-none placeholder:text-sm focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                  type="text"
                  placeholder="Search..."
                >
              </div>
              <div class="max-h-[540px] overflow-y-auto">
                <div
                  v-if="filteredMetricTypes.length"
                  class="w-full my-2"
                >
                  <!-- Metrics -->
                  <h4 class="mb-3 font-medium text-[#68737D]">
                    Standard Metric
                  </h4>
                  <draggable
                    class="flex flex-col gap-2"
                    :list="filteredMetricTypes"
                    :group="{ name: 'metrics', pull: 'clone', put: false }"
                  >
                    <div
                      v-for="{ id, title, year_name } in filteredMetricTypes"
                      :key="`metric-${id}-${year_name}`"
                      class="mx-1 flex items-center justify-between border border-gray-300 px-2 py-3 text-sm"
                    >
                      <div class="flex items-center gap=1-5">
                        <span class="cursor-move text-slate-200">
                          <ThreeBars />
                        </span>
                        <span
                          class="truncate"
                          :title
                        >{{ title }}</span>
                        <span class="px-2 py-1 text-xs font-semibold text-green-800 rounded whitespace-nowrap bg-green-50">
                          {{ year_name }}
                        </span>
                      </div>
                      <button
                        type="button"
                        class="outline-none"
                        @click="previewMetric(id, filteredMetricTypes)"
                      >
                        <EyeIcon custom-style="w-6" />
                      </button>
                    </div>
                  </draggable>
                  <ButtonWithIcon
                    v-if="!metricSearchQuery"
                    classes="w-full mt-3"
                    @click="viewAllMetric()"
                  >
                    {{ showAllMetrics ? "Minimize View" : "View More" }}
                    <template #icon>
                      <ChevronDownLarge :custom-style="`size-5 ${showAllMetrics ? 'rotate-180' : ''}`" />
                    </template>
                  </ButtonWithIcon>
                </div>

                <div
                  v-if="
                    allFilteredMetricTopics.length
                      && metricSearchQuery
                      && filteredMetricTopic !== 'sm'
                  "
                >
                  <!-- Metrics -->
                  <h4 class="my-3 font-medium text-[#68737D]">
                    Filtered Topic Metrics
                  </h4>
                  <draggable
                    class="flex flex-col gap-2"
                    :list="allFilteredMetricTopics"
                    :group="{ name: 'metrics', pull: 'clone', put: false }"
                  >
                    <div
                      v-for="{
                        id,
                        title,
                        year_name,
                      } in allFilteredMetricTopics"
                      :key="`metric-${id}-${year_name}`"
                      class="mx-1 flex items-center justify-between border border-gray-300 px-2 py-3 text-sm"
                    >
                      <div class="flex items-center gap=1-5">
                        <span class="cursor-move text-slate-200">
                          <ThreeBars />
                        </span>
                        <span
                          class="truncate"
                          :title
                        >{{ title }}</span>
                        <span class="px-2 py-1 text-xs font-semibold text-green-800 rounded whitespace-nowrap bg-green-50">
                          {{ year_name }}
                        </span>
                      </div>
                      <button
                        type="button"
                        class="outline-none"
                        @click="previewMetric(id, allFilteredMetricTopics)"
                      >
                        <EyeIcon custom-style="w-6" />
                      </button>
                    </div>
                  </draggable>
                </div>
                <div
                  v-else-if="
                    Number(filteredMetricTopic) > 0 && selectedTopicMetric
                  "
                >
                  <!-- Metrics -->
                  <h4 class="my-3 font-medium text-[#68737D]">
                    {{ selectedTopicMetric.title }}
                  </h4>
                  <draggable
                    class="flex flex-col gap-2"
                    :list="filteredTopicMetrics(selectedTopicMetric)"
                    :group="{ name: 'metrics', pull: 'clone', put: false }"
                  >
                    <div
                      v-for="{ id, title, year_name } in filteredTopicMetrics(
                        selectedTopicMetric,
                      )"
                      :key="`metric-${id}-${year_name}`"
                      class="mx-1 flex items-center justify-between border border-gray-300 px-2 py-3 text-sm"
                    >
                      <div class="flex items-center gap=1-5">
                        <span class="cursor-move text-slate-200">
                          <ThreeBars />
                        </span>
                        <span
                          class="truncate"
                          :title
                        >{{ title }}</span>
                        <span class="px-2 py-1 text-xs font-semibold text-green-800 rounded whitespace-nowrap bg-green-50">
                          {{ year_name }}
                        </span>
                      </div>
                      <button
                        type="button"
                        class="outline-none"
                        @click="previewMetric(id, filteredTopicMetrics(selectedTopicMetric))"
                      >
                        <EyeIcon custom-style="w-6" />
                      </button>
                    </div>
                  </draggable>
                  <ButtonWithIcon
                    classes="w-full mt-3"
                    @click="
                      selectedTopicMetric.row_expanded
                        = !selectedTopicMetric.row_expanded
                    "
                  >
                    {{
                      Number(filteredMetricTopic) > 0
                        && selectedTopicMetric.row_expanded
                        ? "Minimize View"
                        : "View More"
                    }}
                    <template #icon>
                      <ChevronDownLarge
                        :custom-style="`h-5 w-5 ${Number(filteredMetricTopic) > 0 && selectedTopicMetric.row_expanded ? 'rotate-180' : ''}`"
                      />
                    </template>
                  </ButtonWithIcon>
                </div>
                <div v-else>
                  <div
                    v-for="(topic, mtIndex) in topicMetrics"
                    :key="`metric-topic-${topic.id}-${mtIndex}`"
                    class="w-full my-2"
                  >
                    <!-- Metrics -->
                    <h4 class="my-3 font-medium text-[#68737D]">
                      {{ topic.title }}
                    </h4>
                    <draggable
                      class="flex flex-col gap-2"
                      :list="filteredTopicMetrics(topic)"
                      :group="{ name: 'metrics', pull: 'clone', put: false }"
                    >
                      <div
                        v-for="{ id, title, year_name } in filteredTopicMetrics(
                          topic,
                        )"
                        :key="`metric-${id}-${year_name}`"
                        class="mx-1 flex items-center justify-between border border-gray-300 px-2 py-3 text-sm"
                      >
                        <div class="flex items-center gap=1-5">
                          <span class="cursor-move text-slate-200">
                            <ThreeBars />
                          </span>
                          <span
                            class="truncate"
                            :title
                          >{{ title }}</span>
                          <span class="px-2 py-1 text-xs font-semibold text-green-800 rounded whitespace-nowrap bg-green-50">
                            {{ year_name }}
                          </span>
                        </div>
                        <button
                          type="button"
                          class="outline-none"
                          @click="previewMetric(id, filteredTopicMetrics(topic))"
                        >
                          <EyeIcon custom-style="w-6" />
                        </button>
                      </div>
                    </draggable>

                    <ButtonWithIcon
                      classes="w-full mt-3"
                      @click="
                        topicMetrics[mtIndex].row_expanded
                          = !topicMetrics[mtIndex].row_expanded
                      "
                    >
                      {{
                        topicMetrics[mtIndex].row_expanded
                          ? "Minimize View"
                          : "View More"
                      }}
                      <template #icon>
                        <ChevronDownLarge
                          :custom-style="`h-5 w-5 ${topicMetrics[mtIndex].row_expanded ? 'rotate-180' : ''}`"
                        />
                      </template>
                    </ButtonWithIcon>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section
            v-else
            class="sticky z-50 flex-none w-full top-24"
          >
            <!-- Question bank questions -->
            <div class="border-r border-gray-300">
              <div
                id="input-filters"
                class="flex items-center gap-2 mr-1"
              >
                <select
                  v-model="selectedSection"
                  class="w-full h-8 px-3 py-1 text-sm leading-tight text-gray-700 border border-gray-300 outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                  @change="filterBySection"
                >
                  <option
                    value="all"
                    selected
                  >
                    All
                  </option>
                  <option
                    v-for="(section, sectionIndex) in questionSections"
                    :key="`section-${sectionIndex}`"
                    :value="section"
                  >
                    {{ section }}
                  </option>
                </select>
                <input
                  v-model="questionKeyword"
                  class="w-full h-8 px-3 py-1 leading-tight text-gray-700 border border-gray-300 outline-none placeholder:text-sm focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                  type="text"
                  placeholder="Search..."
                >
              </div>
              <draggable
                class="mt-6 flex h-auto max-h-[540px] flex-col gap-1 overflow-y-scroll"
                :list="questions"
                :group="{ name: 'answers', pull: 'clone', put: false }"
              >
                <div
                  v-for="{ id, index, title } in questions"
                  :key="`question-${id}`"
                  class="mx-1 flex items-center justify-between gap-1.5 border border-gray-300 px-2 py-3 text-sm"
                >
                  <div class="flex grow items-center gap-1.5 min-w-0">
                    <span class="cursor-move text-slate-200">
                      <ThreeBars />
                    </span>
                    <span
                      class="truncate min-w-0"
                      :title
                    >{{ title }}</span>
                  </div>
                  <span class="font-semibold shrink-0 whitespace-nowrap">{{ index }}</span>
                </div>
              </draggable>
            </div>
          </section>
        </GeneralTabs>
      </section>
      <section
        id="chapters"
        class="w-3/4 px-4"
      >
        <div class="flex items-start w-full gap-3">
          <div class="w-full">
            <div
              v-for="(chapter, index) in chapters"
              :key="`chapter-${index}`"
            >
              <!-- Chapter -->
              <div
                class="border border-gray-300 p-1.5"
                :class="{
                  'border-l-2 border-l-zuno-turquoise':
                    activeChapter === index && activeFeature === 'chapter',
                }"
                @click.capture="
                  activeFeature = 'chapter';
                  activeChapter = index;
                "
              >
                <div class="flex items-center justify-between gap-6">
                  <input
                    v-model="chapters[index].number"
                    class="w-16 px-4 py-2 leading-tight text-center text-gray-700 border border-gray-300 rounded outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                    type="text"
                    required
                  >
                  <input
                    v-model="chapters[index].title"
                    class="w-full px-4 py-2 leading-tight text-gray-700 border-b border-gray-300 outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                    type="text"
                    placeholder="Chapter title..."
                    required
                  >
                  <button
                    type="button"
                    class="text-gray-400 disabled:cursor-not-allowed"
                    :disabled="chapters.length === 1"
                    @click="deleteChapter(index)"
                  >
                    <TrashLarge />
                  </button>
                </div>
                <input
                  v-model="chapters[index].description"
                  class="w-full px-4 py-2 mt-6 leading-tight text-gray-700 border-b border-gray-300 outline-none placeholder:text-xl focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                  type="text"
                  placeholder="Description..."
                >
              </div>
              <!-- Question statement -->
              <div
                v-show="chapters[index].questions"
                class="flex flex-col justify-center gap-3 mt-3"
                @click.capture="activeFeature = 'question'"
              >
                <draggable
                  :list="chapters[index].questions"
                  group="question-statements"
                  class="flex flex-col gap-2"
                  @change="activeFeature = 'question'"
                >
                  <div
                    v-for="(question, questionIndex) in chapters[index]
                      .questions"
                    :key="`${index}${questionIndex}`"
                    class="flex flex-col px-1 pt-1 pb-3 border border-gray-300"
                    :class="{
                      'border-l-2 border-l-zuno-turquoise':
                        activeFeature === 'question',
                    }"
                  >
                    <div class="flex items-center self-end justify-between w-1/2">
                      <button
                        type="button"
                        class="cursor-move text-slate-200"
                      >
                        <ThreeBars />
                      </button>
                      <button
                        type="button"
                        class="text-gray-400"
                        @click="
                          chapters[index].questions.splice(questionIndex, 1)
                        "
                      >
                        <TrashLarge />
                      </button>
                    </div>
                    <div
                      class="flex items-center justify-between gap-6"
                      :class="{
                        'pb-5':
                          chapters[index].questions[questionIndex].answers
                          && chapters[index].questions[questionIndex].answers
                            .length === 0,
                      }"
                    >
                      <input
                        v-model="chapters[index].questions[questionIndex].number
                        "
                        class="w-16 p-2 leading-tight text-center text-gray-700 border border-gray-300 rounded outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                        type="text"
                        required
                      >
                      <input
                        v-model="chapters[index].questions[questionIndex].title"
                        class="w-full px-4 py-2 leading-tight text-gray-700 truncate border-b border-gray-300 outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                        type="text"
                        required
                      >
                    </div>
                    <draggable
                      class="flex flex-col gap-2"
                      :list="chapters[index].questions[questionIndex].answers"
                      :group="{
                        name: 'question-answers',
                        put: ['answers', 'metrics'],
                      }"
                      @change="
                        activeFeature = 'question';
                        onQuestionAnswersChange($event, questionIndex);
                      "
                    >
                      <div
                        v-for="(answer, answerIndex) in chapters[index]
                          .questions[questionIndex].answers"
                        :key="`${index}${questionIndex}${answerIndex}`"
                      >
                        <div
                          v-if="answer.index"
                          class="flex items-center gap-2"
                        >
                          <p class="w-full px-4 py-2 leading-tight text-gray-700 truncate border-b border-gray-300">
                            {{ answer.title }}
                          </p>
                          <span class="font-semibold whitespace-nowrap">{{
                            answer.index
                          }}</span>
                          <button
                            type="button"
                            class="flex-none text-gray-400"
                            @click="
                              chapters[index].questions[
                                questionIndex
                              ].answers.splice(answerIndex, 1)
                            "
                          >
                            <TrashLarge custom-style="h-4 w-4" />
                          </button>
                        </div>
                        <div
                          v-else
                          class="flex items-center justify-between px-2 py-3 mx-1 mt-1 text-sm border border-gray-300"
                        >
                          <div>
                            <span
                              class="truncate"
                              :title="answer.metricTitle"
                            >{{ answer.metricTitle }}</span>
                            <span
                              class="px-2 py-1 text-xs font-semibold text-green-800 rounded whitespace-nowrap bg-green-50"
                            >{{
                              getYearName(answer.year) }}</span>
                          </div>
                          <button
                            type="button"
                            class="flex-none text-gray-400"
                            @click="
                              chapters[index].questions[
                                questionIndex
                              ].answers.splice(answerIndex, 1)
                            "
                          >
                            <TrashLarge custom-style="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </draggable>
                  </div>
                </draggable>
              </div>
              <!-- Subchapters -->
              <div
                v-for="(subchapter, subchapterIndex) in chapters[index]
                  .subchapters"
                v-show="chapters[index].subchapters !== null"
                :key="`subchapter-${subchapterIndex}`"
                @click.capture="
                  activeSubchapterIndex = subchapterIndex;
                  activeFeature = 'subchapter';
                  activeChapter = index;
                "
              >
                <div
                  class="mt-3 flex items-center justify-between gap-6 border border-gray-300 p-1.5"
                  :class="{
                    'border-l-2 border-l-zuno-turquoise':
                      activeChapter === index
                      && activeFeature === 'subchapter'
                      && activeSubchapterIndex === subchapterIndex,
                  }"
                >
                  <input
                    v-model="chapters[index].subchapters[subchapterIndex].title"
                    class="w-full px-4 py-2 leading-tight text-gray-700 border-b border-gray-300 outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                    type="text"
                    placeholder="Subchapter title..."
                  >
                  <button
                    type="button"
                    class="flex-none text-gray-400"
                    @click="deleteSubchapter(subchapterIndex, index)"
                  >
                    <TrashLarge />
                  </button>
                </div>
                <!-- Subchapter questions -->
                <div
                  v-show="chapters[index].subchapters[subchapterIndex].questions
                  "
                  class="flex flex-col justify-center gap-3 pl-6 mt-3"
                >
                  <draggable
                    :list="chapters[index].subchapters[subchapterIndex].questions
                    "
                    group="subchapter-question-statements"
                    class="flex flex-col gap-2"
                    @change="
                      activeSubchapterIndex = subchapterIndex;
                      activeFeature = 'subchapter';
                      activeChapter = index;
                    "
                  >
                    <div
                      v-for="(question, questionIndex) in chapters[index]
                        .subchapters[subchapterIndex].questions"
                      :key="`subchapterquestion-${questionIndex}`"
                      class="flex flex-col px-1 pt-1 pb-3 border border-gray-300"
                      :class="{
                        'border-l-2 border-l-zuno-turquoise':
                          activeChapter === index
                          && activeFeature === 'subchapter'
                          && activeSubchapterIndex === subchapterIndex,
                      }"
                    >
                      <div class="flex items-center self-end justify-between w-1/2">
                        <button
                          type="button"
                          class="cursor-move text-slate-200"
                        >
                          <ThreeBars />
                        </button>
                        <button
                          type="button"
                          class="text-gray-400"
                          @click="
                            chapters[index].subchapters[
                              subchapterIndex
                            ].questions.splice(questionIndex, 1)
                          "
                        >
                          <TrashLarge />
                        </button>
                      </div>
                      <div
                        class="flex items-center justify-between gap-6"
                        :class="{
                          'pb-5':
                            chapters[index].subchapters[subchapterIndex]
                              .questions[questionIndex].answers
                            && chapters[index].subchapters[subchapterIndex]
                              .questions[questionIndex].answers.length === 0,
                        }"
                      >
                        <input
                          v-model="chapters[index].subchapters[subchapterIndex]
                            .questions[questionIndex].number
                          "
                          class="w-16 p-2 leading-tight text-center text-gray-700 border border-gray-300 rounded outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                          type="text"
                          required
                        >
                        <input
                          v-model="chapters[index].subchapters[subchapterIndex]
                            .questions[questionIndex].title
                          "
                          class="w-full px-4 py-2 leading-tight text-gray-700 truncate border-b border-gray-300 outline-none focus:rounded focus:ring-1 focus:ring-green-300 focus:ring-offset-1"
                          type="text"
                          required
                        >
                      </div>
                      <draggable
                        class="flex flex-col gap-2"
                        :list="chapters[index].subchapters[subchapterIndex]
                          .questions[questionIndex].answers
                        "
                        :group="{
                          name: 'subchapter-question-answers',
                          put: ['answers', 'metrics'],
                        }"
                        @change="
                          activeFeature = 'subchapter';
                          onSubchapterQuestionAnswersChange(
                            $event,
                            subchapterIndex,
                            questionIndex,
                          );
                        "
                      >
                        <div
                          v-for="(answer, answerIndex) in chapters[index]
                            .subchapters[subchapterIndex].questions[
                              questionIndex
                            ].answers"
                          :key="`${index}${questionIndex}${answerIndex}`"
                        >
                          <!-- Indexed questions -->
                          <div
                            v-if="answer.index"
                            class="flex items-center gap-2"
                          >
                            <p class="w-full px-4 py-2 leading-tight text-gray-700 truncate border-b border-gray-300">
                              {{ answer.title }}
                            </p>
                            <span class="font-semibold whitespace-nowrap">{{
                              answer.index
                            }}</span>
                            <button
                              type="button"
                              class="flex-none text-gray-400"
                              @click="
                                chapters[index].subchapters[
                                  subchapterIndex
                                ].questions[questionIndex].answers.splice(
                                  answerIndex,
                                  1,
                                )
                              "
                            >
                              <TrashLarge custom-style="h-4 w-4" />
                            </button>
                          </div>
                          <!-- Metrics -->
                          <div
                            v-else
                            class="flex items-center justify-between px-2 py-3 mx-1 mt-1 text-sm border border-gray-300"
                          >
                            <div>
                              <span
                                class="truncate"
                                :title="answer.metricTitle"
                              >{{ answer.metricTitle }}</span>
                              <span
                                class="px-2 py-1 text-xs font-semibold text-green-800 rounded whitespace-nowrap bg-green-50"
                              >{{
                                getYearName(answer.year) }}</span>
                            </div>
                            <button
                              type="button"
                              class="flex-none text-gray-400"
                              @click="
                                chapters[index].subchapters[
                                  subchapterIndex
                                ].questions[questionIndex].answers.splice(
                                  answerIndex,
                                  1,
                                )
                              "
                            >
                              <TrashLarge custom-style="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </draggable>
                    </div>
                  </draggable>
                </div>
              </div>
            </div>
          </div>
          <div class="sticky flex flex-col gap-2 p-1 mr-4 border border-gray-300 top-24 max-w-min text-slate-900">
            <button
              type="button"
              title="Add chapter"
              @click="addChapter()"
            >
              <FourSquares />
            </button>
            <button
              type="button"
              title="Add subchapter"
              @click="addSubchapter()"
            >
              <span class="text-xl font-bold capitalise">T</span>
            </button>
            <button
              type="button"
              title="Add question"
              @click="addQuestions()"
            >
              <PlusRoundedIcon />
            </button>
          </div>
        </div>
      </section>
    </div>

    <div class="flex justify-end mt-6">
      <button
        type="button"
        class="px-6 py-2 mr-3 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-gray-500 border border-gray-500 focus:shadow-outline-gray hover:bg-gray-400 focus:outline-none disabled:cursor-not-allowed"
        :disabled="cantSaveTemplate"
        @click="saveData('draft')"
      >
        Save as Draft
      </button>
      <button
        type="button"
        class="px-6 py-2 mr-3 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-gray-500 border border-gray-500 focus:shadow-outline-gray hover:bg-gray-400 focus:outline-none"
        @click="$emit('stepBack')"
      >
        Back
      </button>
      <button
        type="submit"
        :disabled="cantSaveTemplate"
        class="px-6 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-green-600 border border-transparent focus:shadow-outline-green hover:bg-green-700 focus:outline-none disabled:cursor-not-allowed disabled:bg-green-600/80"
      >
        Save
      </button>
    </div>
    <!-- Metric Preview -->
    <ModalComponent
      name="previewMetric"
      class="rounded-full"
      height="auto"
      :scrollable="true"
      :width="700"
      :adaptive="true"
    >
      <div class="p-6 space-y-6">
        <ModalHeader
          :title="metricToPreview?.title"
          subtitle="Metric table preview"
          @close="endPreview"
        />
        <MetricTable
          v-if="metricToPreview"
          :unique-id="metricToPreview.id"
          :table="metricToPreview.metric_table"
        />
      </div>
    </ModalComponent>
  </form>
</template>

<script>
// import draggable from 'vuedraggable'
import { VueDraggableNext } from 'vue-draggable-next'

export default {
  name: 'ReportingFrameworksTemplateBuilder',

  components: {
    draggable: VueDraggableNext,
  },

  props: {
    savedChapters: {
      type: Array,
      required: true,
    },
  },

  emits: ['dataSaved', 'dataDrafted', 'stepBack'],

  data() {
    const metricYears = [
      {
        id: 'base_year',
        name: 'Base Year',
      },
      {
        id: 'reporting_year',
        name: 'Reporting Year',
      },
    ]

    return {
      questions: [],
      unfilteredQuestions: [],
      chapters: this.savedChapters,
      activeChapter: 0,
      activeFeature: 'chapter',
      activeSubchapterIndex: 0,
      viewTab: 'AMOQ',
      selectedSection: 'all',
      questionSections: [],
      questionKeyword: '',
      metricSearchQuery: '',
      metricYears,
      yearType: metricYears[0].id,
      metricTypes: [],
      topicMetrics: [],
      selectedTopicMetric: [],
      filteredMetricTypes: [],
      unfilteredMetricTopic: [],
      allFilteredMetricTopics: [],
      filteredMetricTopic: 'all',
      currentSelectedMetric: null,
      showAllMetrics: false,
      metricToPreview: null,
    }
  },

  computed: {
    cantSaveTemplate() {
      return this.chapters.some(
        ({ title, questions }) =>
          !title || questions?.some(({ number, title }) => !number || !title),
      )
    },
    currentYearName() {
      return this.metricYears.find(({ id }) => id === this.yearType).name
    },
    allMetrics() {
      return this.unfilteredMetricTopic.reduce((allMetrics, topic) => {
        const metricsWithSectionId = topic.metrics.map(metric => ({
          ...metric,
          topic_id: topic.id,
        }))
        return allMetrics.concat(metricsWithSectionId)
      }, [])
    },
  },

  watch: {
    savedChapters(newChapters) {
      this.chapters = newChapters.length
        ? newChapters
        : [
            {
              number: 1,
              title: '',
              description: '',
              subchapters: null,
              questions: null,
            },
          ]
    },
    questionKeyword(newKeyword) {
      if (newKeyword === '') {
        this.questions = [...this.unfilteredQuestions].filter(({ section }) =>
          this.conditionToFilterSection(section),
        )
      }
      else {
        this.filterByKeywordAndSection()
      }
    },
    metricSearchQuery(newKeyword) {
      this.filteredMetricTypes = [...this.metricTypes].filter(({ title }) =>
        newKeyword === ''
          ? true
          : title.toLowerCase().includes(newKeyword.toLowerCase()),
      )
      // Filter Topic Metrics
      if (newKeyword !== '') {
        const filteredArray
          = Number(this.filteredMetricTopic) > 0
            ? [...this.allMetrics].filter(
                ({ name, topic_id }) =>
                  name.toLowerCase().includes(newKeyword.toLowerCase())
                  && Number(topic_id) === Number(this.filteredMetricTopic),
              )
            : [...this.allMetrics].filter(({ name }) =>
                newKeyword === ''
                  ? true
                  : name.toLowerCase().includes(newKeyword.toLowerCase()),
              )
        const metricTopic = []
        this.metricYears.forEach((year) => {
          filteredArray.forEach((metric) => {
            metricTopic.push({
              id: metric.metric_id,
              title: metric.name,
              year_id: year.id,
              metricType: 'metric',
              year_name: year.name,
              metric_table: metric.metric_table,
            })
          })
        })
        this.allFilteredMetricTopics = metricTopic.sort((a, b) =>
          a.title
            .trim()
            .toLowerCase()
            .localeCompare(b.title.trim().toLowerCase()),
        )
      }
    },
    filteredMetricTopic(topicId) {
      this.selectedTopicMetric = []
      this.allFilteredMetricTopics = []
      if (topicId === 'sm') {
        this.topicMetrics = []
        this.showAllMetrics = true
        this.filteredMetricTypes = [...this.metricTypes].filter(({ title }) =>
          this.metricSearchQuery === ''
            ? true
            : title.toLowerCase().includes(this.metricSearchQuery.toLowerCase()),
        )
      }
      else if (Number(topicId) > 0) {
        const objectIndex = this.unfilteredMetricTopic.findIndex(
          item => item.id === Number(topicId),
        )
        if (objectIndex !== -1) {
          this.currentSelectedMetric = Number(topicId)
          this.filteredMetricTypes = []
          this.unfilteredMetricTopic[objectIndex].row_expanded = true
          this.selectedTopicMetric = this.unfilteredMetricTopic[objectIndex]
          this.selectedTopicMetric.row_expanded = true
        }
      }
      else {
        this.filteredMetricTypes = [...this.metricTypes].filter(({ title }) =>
          this.metricSearchQuery === ''
            ? true
            : title.toLowerCase().includes(this.metricSearchQuery.toLowerCase()),
        )
        this.topicMetrics = [...this.unfilteredMetricTopic]
      }
    },
  },

  mounted() {
    Promise.all([
      this.$apis.questions.listAll(),
      this.$apis.questions.listStandardMetricsWithCategories(),
      this.$apis.questions.listTopicMetrics(),
    ]).then((responses) => {
      const [
        { data: questionsData },
        { data: standardMetricData },
        { data: topicMetricsData },
      ] = responses

      const transformedQuestions = questionsData
        .filter(({ status }) => status === 'published')
        .map((question) => {
          this.questionSections.push(question.section)
          question.number = null
          return question
        })

      this.questions = transformedQuestions
      this.questionSections = [...new Set(this.questionSections)]
      this.unfilteredQuestions = this.questions

      this.metricYears.forEach((year) => {
        standardMetricData.forEach((metric) => {
          this.metricTypes.push({
            id: metric.id,
            title: metric.name,
            metricType: Number(metric.id) > 0 ? 'category' : 'standard',
            year_id: year.id,
            year_name: year.name,
            metric_table: metric.metric_table,
          })
        })
      })
      this.metricTypes = this.metricTypes.sort((a, b) =>
        a.title
          .trim()
          .toLowerCase()
          .localeCompare(b.title.trim().toLowerCase()),
      )
      this.filteredMetricTypes = this.metricTypes.slice(0, 4)

      const metricsTopticData = topicMetricsData.filter(
        item => item.metrics.length > 0,
      )

      this.topicMetrics = metricsTopticData.map((topic) => {
        topic.row_expanded = false
        return topic
      })
      this.unfilteredMetricTopic = this.topicMetrics
    })
  },

  methods: {
    previewMetric(metricId, haystack) {
      this.metricToPreview = haystack.find(({ id }) => id === metricId)
      nextTick(() => {
        if (this.metricToPreview.metric_table === undefined) {
          this.$toast.info(`${this.metricToPreview.title} has no metric table defined!`)
        }
        else {
          this.$modal.show('previewMetric')
        }
      })
    },
    endPreview() {
      this.metricToPreview = null
      this.$modal.hide('previewMetric')
    },
    onQuestionAnswersChange(event, qnIndex) {
      if (event.added && !Object.keys(event.added.element).includes('index')) {
        const { id, title, year_id, metricType } = event.added.element
        this.chapters[this.activeChapter].questions[qnIndex].answers[
          event.added.newIndex
        ] = {
          isMetric: true,
          metricId: id,
          metricTitle: title,
          year: year_id,
          type: metricType,
        }
        getCurrentInstance()?.proxy?.$forceUpdate()
      }
    },
    onSubchapterQuestionAnswersChange(event, subchapterIndex, qnIndex) {
      if (event.added && !Object.keys(event.added.element).includes('index')) {
        const { id, title, year_id, metricType } = event.added.element
        this.chapters[this.activeChapter].subchapters[
          subchapterIndex
        ].questions[qnIndex].answers[event.added.newIndex] = {
          isMetric: true,
          metricId: id,
          metricTitle: title,
          year: year_id,
          type: metricType,
        }
        getCurrentInstance()?.proxy?.$forceUpdate()
      }
    },
    getYearName(yearType) {
      return this.metricYears.find(({ id }) => id === yearType)?.name
    },
    filterBySection(event) {
      const section = event.target.value
      if (section === 'all') {
        this.questions = [...this.unfilteredQuestions].filter(({ title }) =>
          this.conditionToSearchQuestion(title),
        )
      }
      else {
        this.filterByKeywordAndSection()
      }
    },
    filterByKeywordAndSection() {
      this.questions = [...this.unfilteredQuestions].filter(
        ({ section, title }) =>
          this.conditionToSearchQuestion(title)
          && this.conditionToFilterSection(section),
      )
    },
    conditionToSearchQuestion(title) {
      return this.questionKeyword !== ''
        ? title.toLowerCase().includes(this.questionKeyword.toLowerCase())
        : true
    },
    conditionToFilterSection(section) {
      return this.selectedSection !== 'all'
        ? section === this.selectedSection
        : true
    },
    saveData(status) {
      let eventName = null
      if (status === 'publish') {
        eventName = 'dataSaved'
      }
      else if (status === 'draft') {
        eventName = 'dataDrafted'
      }
      this.$emit(eventName, {
        chapters: this.transformChapters([...this.chapters]),
      })
    },
    transformChapters(data) {
      return data.map((chapter) => {
        chapter.questions
          = chapter.questions?.map(({ number, answers, title }) => {
            return {
              number,
              title,
              answers:
                answers?.map(
                  ({
                    id,
                    title,
                    index,
                    isMetric,
                    metricId,
                    metricTitle,
                    year,
                    type,
                  }) => {
                    return (
                      {
                        ...(!isMetric && { id, title, index }),
                        ...(isMetric && {
                          isMetric: true,
                          metricId,
                          metricTitle,
                          year,
                          type,
                        }),
                      }
                    )
                  },
                ) ?? [],
            }
          }) ?? null
        chapter.subchapters
          = chapter.subchapters?.map(({ title, questions }) => {
            return {
              title,
              questions:
                questions?.map(({ number, title, answers }) => {
                  return {
                    number,
                    title,
                    answers:
                      answers?.map(
                        ({
                          id,
                          title,
                          index,
                          isMetric,
                          metricId,
                          metricTitle,
                          year,
                          type,
                        }) => {
                          return (
                            {
                              ...(!isMetric && { id, title, index }),
                              ...(isMetric && {
                                isMetric: true,
                                metricId,
                                metricTitle,
                                year,
                                type,
                              }),
                            }
                          )
                        },
                      ) ?? [],
                  }
                }) ?? null,
            }
          }) ?? null
        return chapter
      })
    },
    addChapter() {
      this.chapters.push({
        number: this.chapters.length + 1,
        title: '',
        description: '',
        subchapters: [
          {
            title: '',
            questions: null,
          },
        ],
        questions: null,
      })
      this.activeChapter = this.chapters.length - 1
      this.activeFeature = 'chapter'
    },
    deleteChapter(index) {
      this.chapters.splice(index, 1)
      this.activeChapter = this.chapters.length - 1
    },
    addSubchapter() {
      this.activeFeature = 'subchapter'
      if (!this.chapters[this.activeChapter].subchapters) {
        this.chapters[this.activeChapter].subchapters = [
          {
            title: '',
            questions: null,
          },
        ]
      }
      else {
        this.chapters[this.activeChapter].subchapters.push({
          title: '',
          questions: null,
        })
        this.activeSubchapterIndex
          = this.chapters[this.activeChapter].subchapters.length - 1
      }
    },
    deleteSubchapter(subchapterIndex, chapterIndex) {
      this.chapters[chapterIndex].subchapters.splice(subchapterIndex, 1)
      if (!this.chapters[chapterIndex].subchapters.length) {
        this.chapters[chapterIndex].subchapters = null
      }
      this.activeSubchapterIndex = !this.chapters[chapterIndex].subchapters
        ?.length
        ? 0
        : this.chapters[this.activeChapter].subchapters.length - 1
    },
    addQuestions() {
      const emptyQuestion = { number: '', title: '', answers: [] }
      if (this.activeFeature === 'subchapter') {
        return this.chapters[this.activeChapter].subchapters[
          this.activeSubchapterIndex
        ].questions !== null
          ? this.chapters[this.activeChapter].subchapters[
            this.activeSubchapterIndex
          ].questions.push(emptyQuestion)
          : (this.chapters[this.activeChapter].subchapters[
              this.activeSubchapterIndex
            ].questions = [emptyQuestion])
      }

      if (
        this.chapters[this.activeChapter].questions
        && this.chapters[this.activeChapter].questions.length
      ) {
        this.chapters[this.activeChapter].questions.push(emptyQuestion)
      }
      else {
        this.chapters[this.activeChapter].questions = [emptyQuestion]
      }
      this.activeFeature = 'questions'
    },
    viewAllMetric() {
      this.showAllMetrics = !this.showAllMetrics
      this.filteredMetricTypes = !this.showAllMetrics
        ? this.metricTypes.slice(0, 4)
        : this.metricTypes
    },
    filteredTopicMetrics(data) {
      const metricTopic = []
      const loadMetricData = data.row_expanded
        ? data.metrics
        : data.metrics.slice(0, 2)
      this.metricYears.forEach((year) => {
        loadMetricData.forEach((metric) => {
          metricTopic.push({
            topic_id: data.id,
            id: metric.metric_id,
            title: metric.name,
            year_id: year.id,
            metricType: 'metric',
            year_name: year.name,
            metric_table: metric.metric_table,
          })
        })
      })
      return metricTopic.sort((a, b) =>
        a.title
          .trim()
          .toLowerCase()
          .localeCompare(b.title.trim().toLowerCase()),
      )
    },
  },
}
</script>
